import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import Table from '../../../table/Table';
import Pagination from '../../../pagination/Pagination';
import FormCheckbox from '../../forms/FormCheckBox';
import { useGetStreetsQuery } from '../../../../redux/features/community-management/communityApi';
import type { GetStreet } from '../../../../redux/features/community-management/communityTypes';
import UserStorage from '../../../../shared/utils/userStorage';

type TableColumn<T> = {
  key: keyof T;
  label: string | ReactNode;
  render?: (value: T[keyof T], row: T) => ReactNode;
};

type FormValues = {
  selectedPositions: Record<string, boolean>;
};

const EstateStreetsTable = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  const community_id = UserStorage.getCommunityId();
  const { data: streetRespondData, isLoading } = useGetStreetsQuery(
    { community_id: community_id || '' },
    { skip: !community_id },
  );

  const streetData = useMemo(
    () => (streetRespondData?.data?.data as GetStreet[]) || [],
    [streetRespondData?.data?.data],
  );

  const methods = useForm<FormValues>({
    defaultValues: {
      selectedPositions: streetData.reduce(
        (acc, item) => ({
          ...acc,
          [item.id]: false,
        }),
        {},
      ),
    },
  });

  const { control, setValue } = methods;
  const selectedPositions = useWatch({ control, name: 'selectedPositions' });

  // Handle select all/deselect all checkbox
  const handleSelectAll = (checked: boolean) => {
    const newSelection = streetData.reduce(
      (acc, item) => ({ ...acc, [item.id]: checked }),
      {},
    );
    setValue('selectedPositions', newSelection);
  };

  // Check if all visible rows are selected
  const allSelected =
    streetData.length > 0 &&
    streetData.every((item) => selectedPositions[item.id]);

  // Check if some (but not all) rows are selected
  const indeterminate =
    !allSelected && streetData.some((item) => selectedPositions[item.id]);

  const columns: TableColumn<GetStreet>[] = [
    {
      key: 'id',
      label: (
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={allSelected}
            ref={(input) => {
              if (input) {
                input.indeterminate = indeterminate;
              }
            }}
            onChange={(e) => handleSelectAll(e.target.checked)}
            className="hidden md:block appearance-none h-4 w-4 text-pry border border-border bg-gray-100 rounded-sm focus:ring-active focus:ring-2 focus:outline-none checked:focus:ring-border focus:ring-offset-2 focus:ring-offset-gray-100 checked:bg-pry checked:border-transparent checked:focus:ring-offset-gray-100"
          />
          <span>Street Name</span>
        </div>
      ),
      render: (_, row) => (
        <div className="flex items-center gap-3">
          <div onClick={(e) => e.stopPropagation()}>
            <FormCheckbox<FormValues>
              name={`selectedPositions.${row.id}`}
              control={methods.control}
            />
          </div>
          <span className="text-dark-text font-medium font-libre capitalize">
            {row.name}
          </span>
        </div>
      ),
    },
    {
      key: 'id',
      label: 'Start Number',
      render: (_, value) => (
        <span className="text-text-light text-sm font-medium font-libre ">
          {value.starting_number ? value.starting_number : 'N/A'}
        </span>
      ),
    },
    {
      key: 'id',
      label: 'End Number',
      render: (_, value) => (
        <span className="text-text-light text-sm font-medium font-libre ">
          {value.ending_number ? value.ending_number : 'N/A'}
        </span>
      ),
    },
  ];



  return (
    <div>
      <FormProvider {...methods}>
        <div className="sm:border border-border rounded-xl">
          <div className="mb-5">
            <Table
              data={streetData}
              columns={columns}
              onRowClick={(row) =>
                navigate(`/community-management/street/${row.id}`)
              }
              loading={isLoading}
            />
          </div>
          {
            streetData.length>0 && (
              <Pagination
                totalPages={streetRespondData?.data.meta.totalPages ?? 1}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                maxLength={10}
              />
            )
          }
        </div>
      </FormProvider>
    </div>
  );
};

export default EstateStreetsTable;
